import React, { useDeferredValue } from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
  ScriptableContext,
} from 'chart.js';
import { useQuery } from 'react-query';
import { client } from 'src/utils/api-client';
import { useAppSelector } from 'src/redux-file/hooks';
import { StyledDiagramContainer, StyledLabel } from './index.styles';
import { useTranslation } from 'react-i18next';
import { Button, Typography } from '@mui/material';
import ArrowOutwardOutlinedIcon from '@mui/icons-material/ArrowOutwardOutlined';
import {
  ScenarioControls,
  ScenarioImpact,
  ScenarioSimulationResponse,
} from '../analytics';
import { getLcaApiErrorMessage } from 'src/Lca/apiErrors';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface MonteCarloDiagramProps {
  productId: number;
  scenarioControls: ScenarioControls;
  compact?: boolean;
  onOpenFullAnalytics?: () => void;
}

interface CustomScriptableContext extends ScriptableContext<'line'> {
  xStarted?: boolean;
  yStarted?: boolean;
}

const getScenarioScale = (result?: ScenarioImpact) => {
  if (!result || result.baseline_total === 0) {
    return 1;
  }

  return result.scenario_total / result.baseline_total;
};

const scaleSeries = (values: number[] | undefined, scale: number) =>
  values?.map((value) => value * scale) || [];

const MonteCarloDiagram: React.FC<MonteCarloDiagramProps> = ({
  productId,
  scenarioControls,
  compact = false,
  onOpenFullAnalytics,
}) => {
  const { selectedAreaOfImpact } = useAppSelector((state) => state.lcaDiagram);
  const { t } = useTranslation('lca');
  const deferredTargetRecycledSharePct = useDeferredValue(
    scenarioControls.targetRecycledSharePct
  );
  const deferredMaterialReductionPct = useDeferredValue(
    scenarioControls.materialReductionPct
  );
  const deferredTransportReductionPct = useDeferredValue(
    scenarioControls.transportReductionPct
  );

  const monteCarloQuery = useQuery(
    [
      'lca-diagram',
      'calculations',
      'monte-carlo-diagram',
      { productId, selectedAreaOfImpact },
    ],
    () =>
      client.get(`/web/lca/monte_carlo_chart/${productId}`, {
        params: { area_id: selectedAreaOfImpact?.id },
      }),
    {
      enabled: !!selectedAreaOfImpact,
    }
  );

  const scenarioSimulationQuery = useQuery(
    [
      'lca-diagram',
      'analytics',
      'scenario-simulation',
      {
        productId,
        selectedAreaOfImpact,
        deferredTargetRecycledSharePct,
        deferredMaterialReductionPct,
        deferredTransportReductionPct,
      },
    ],
    () =>
      client
        .get<ScenarioSimulationResponse>(
          `/web/lca/scenario_simulation/${productId}`,
          {
            params: {
              area_id: selectedAreaOfImpact?.id,
              target_recycled_share_pct: deferredTargetRecycledSharePct,
              material_reduction_pct: deferredMaterialReductionPct,
              transport_reduction_pct: deferredTransportReductionPct,
            },
          }
        )
        .then((response) => response.data),
    {
      enabled: !!selectedAreaOfImpact,
    }
  );

  const { isLoading, data, error } = monteCarloQuery;
  const scenarioData = scenarioSimulationQuery.data;
  const calculationErrorMessage =
    getLcaApiErrorMessage(error, t) ||
    getLcaApiErrorMessage(scenarioSimulationQuery.error, t);

  if (!selectedAreaOfImpact) {
    return null;
  }

  if (isLoading)
    return (
      <div
        style={{
          height: 215,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {t('loading')}
      </div>
    );
  if (calculationErrorMessage) {
    return (
      <StyledDiagramContainer>
        <StyledLabel>
          {t('monteCarloSimulation')} [{selectedAreaOfImpact?.unit_name}]
        </StyledLabel>
        <Typography color="error">{calculationErrorMessage}</Typography>
      </StyledDiagramContainer>
    );
  }
  if (!isLoading) {
    const baselineData = data?.data.data || [];
    const highestAverages = data?.data.data_plus_std || [];
    const lowestAverages = data?.data.data_minus_std || [];

    const totalDuration = compact ? 0 : 10000;

    const delayBetweenPoints = baselineData.length
      ? totalDuration / baselineData.length
      : 0;

    const finalAverage =
      baselineData.length > 0
        ? baselineData[baselineData.length - 1]
        : undefined;
    const finalUpper =
      highestAverages.length > 0
        ? highestAverages[highestAverages.length - 1]
        : undefined;
    const finalLower =
      lowestAverages.length > 0
        ? lowestAverages[lowestAverages.length - 1]
        : undefined;

    const recycledScale = getScenarioScale(scenarioData?.recycled_content);
    const lightweightScale = getScenarioScale(scenarioData?.lightweighting);
    const transportScale = getScenarioScale(
      scenarioData?.transport_optimization
    );

    const previousY = (ctx: any) =>
      ctx.index === 0
        ? ctx.chart.scales.y.getPixelForValue(100)
        : ctx.chart
            .getDatasetMeta(ctx.datasetIndex)
            .data[ctx.index - 1]?.getProps(['y'], true).y;

    const scenarioDatasets = scenarioData
      ? [
          {
            label: t('scenarioExplorer.recycled.title', {
              defaultValue: 'Recycled content',
            }),
            data: scaleSeries(baselineData, recycledScale),
            borderColor: '#2E7D32',
            borderDash: [8, 6],
            tension: 0.1,
            pointRadius: 0,
          },
          {
            label: t('scenarioExplorer.lightweighting.title', {
              defaultValue: 'Lightweighting',
            }),
            data: scaleSeries(baselineData, lightweightScale),
            borderColor: '#1565C0',
            borderDash: [8, 6],
            tension: 0.1,
            pointRadius: 0,
          },
          {
            label: t('scenarioExplorer.transport.title', {
              defaultValue: 'Transport optimization',
            }),
            data: scaleSeries(baselineData, transportScale),
            borderColor: '#C62828',
            borderDash: [8, 6],
            tension: 0.1,
            pointRadius: 0,
          },
        ]
      : [];

    const renderData: ChartData<'line'> = {
      labels: Array.from({ length: baselineData.length }, (_, i) => i + 1),
      datasets: [
        {
          label: t('monteCarloSimulation'),
          data: baselineData,
          borderColor: '#CC8720',
          tension: 0.1,
          pointRadius: 2,
        },
        {
          label: t('standardDeviationTop'),
          data: highestAverages,
          borderColor: '#B8DBE5',
          tension: 0.1,
          fill: '+1',
          pointRadius: 0,
        },
        {
          label: t('standardDeviationBottom'),
          data: lowestAverages,
          borderColor: '#B8DBE5',
          tension: 0.1,
          fill: '-1',
          backgroundColor: 'rgba(184, 219, 229, 0.4)',
          pointRadius: 0,
        },
        ...scenarioDatasets,
      ],
    };

    const options: ChartOptions<'line'> = {
      maintainAspectRatio: !compact,
      animations: {
        x: {
          type: 'number',
          easing: 'linear',
          duration: delayBetweenPoints,
          from: NaN,
          delay(ctx: ScriptableContext<'line'>) {
            const customCtx = ctx as CustomScriptableContext;

            const { index } = ctx as any;
            if (ctx.type !== 'data' || customCtx.xStarted) {
              return 0;
            }
            customCtx.xStarted = true;
            return index * delayBetweenPoints;
          },
        },
        y: {
          type: 'number',
          easing: 'linear',
          duration: delayBetweenPoints,
          from: previousY,
          delay(ctx: ScriptableContext<'line'>) {
            const customCtx = ctx as CustomScriptableContext;
            const { index } = ctx as any;

            if (ctx.type !== 'data' || customCtx.yStarted) {
              return 0;
            }
            customCtx.yStarted = true;
            return index * delayBetweenPoints;
          },
        },
      },
      plugins: {
        legend: {
          display: !compact,
        },
      },
      scales: {
        x: {
          title: {
            display: !compact,
            text: t('monteCarloAxes.x', {
              defaultValue: 'Simulation run',
            }),
            color: '#6B5B4D',
          },
        },
        y: {
          title: {
            display: !compact,
            text: t('monteCarloAxes.y', {
              defaultValue: 'Impact result [{{unit}}]',
              unit: selectedAreaOfImpact?.unit_name || '',
            }),
            color: '#6B5B4D',
          },
        },
      },
    };
    return (
      <StyledDiagramContainer
        style={
          compact
            ? {
                padding: 24,
                marginTop: 16,
              }
            : undefined
        }
      >
        {compact ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 12,
              marginBottom: 12,
            }}
          >
            <div>
              <StyledLabel
                style={{ padding: 0, fontSize: 18, lineHeight: '24px' }}
              >
                {t('monteCarloSimulation')}
              </StyledLabel>
              <div style={{ color: '#6B5B4D', fontSize: 14, marginTop: 8 }}>
                {finalAverage !== undefined &&
                finalLower !== undefined &&
                finalUpper !== undefined
                  ? t('scenarioExplorer.monteCarloCompactSummary', {
                      defaultValue:
                        'Average estimate {{mean}} with an uncertainty band from {{low}} to {{high}} {{unit}}.',
                      mean: finalAverage.toFixed(2),
                      low: finalLower.toFixed(2),
                      high: finalUpper.toFixed(2),
                      unit: selectedAreaOfImpact?.unit_name || '',
                    })
                  : t('loading')}
              </div>
            </div>
            {onOpenFullAnalytics && (
              <Button
                variant="secondary"
                onClick={onOpenFullAnalytics}
                endIcon={<ArrowOutwardOutlinedIcon />}
              >
                {t('scenarioExplorer.openFull', {
                  defaultValue: 'Open full analytics',
                })}
              </Button>
            )}
          </div>
        ) : (
          <>
            <StyledLabel>
              {t('monteCarloSimulation')} [{selectedAreaOfImpact?.unit_name}]
            </StyledLabel>
            <div style={{ color: '#6B5B4D', marginBottom: 12 }}>
              {t('scenarioExplorer.description', {
                defaultValue:
                  'Estimated what-if scenarios based on the current stage footprint. These scenarios scale the most relevant life-cycle stages instead of recalculating the full product model.',
              })}
            </div>
          </>
        )}
        <div style={compact ? { height: 220 } : undefined}>
          <Line data={renderData} options={options} />
        </div>
      </StyledDiagramContainer>
    );
  }
  return (
    <div>
      {t('error')}: {error}
    </div>
  );
};

export default MonteCarloDiagram;
